import { cn } from '@/lib/utils';
import { Check } from 'lucide-react';
import { motion } from 'framer-motion';

interface StepIndicatorProps {
	step: number;
	totalSteps: number;
	labels?: string[];
}

const StepCircle = ({ isCompleted, isCurrent, stepNumber }: {
	isCompleted: boolean;
	isCurrent: boolean;
	stepNumber: number;
}) => (
	<motion.div
		layout
		className={cn(
			'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 text-xs font-semibold',
			isCompleted && 'border-primary bg-primary text-primary-foreground',
			isCurrent && 'border-primary bg-primary/10 text-primary',
			!isCompleted && !isCurrent && 'border-muted bg-muted/50 text-muted-foreground',
		)}
		transition={{ duration: 0.3 }}
	>
		{isCompleted ? (
			<motion.span
				key="check"
				initial={{ scale: 0 }}
				animate={{ scale: 1 }}
				transition={{ type: 'spring', stiffness: 300, damping: 20 }}
			>
				<Check className="h-4 w-4" />
			</motion.span>
		) : stepNumber}
	</motion.div>
);

/** Numbered step indicator with connecting lines, check marks, and optional labels. */
export function StepIndicator({ step, totalSteps, labels }: StepIndicatorProps) {
	return (
		<div className="flex items-start justify-center gap-0">
			{Array.from({ length: totalSteps }, (_, i) => {
				const stepNumber = i + 1;
				const isCompleted = stepNumber < step;
				const isCurrent = stepNumber === step;
				const label = labels?.[i];
				return (
					<div key={i} className="flex items-start">
						<div className="flex flex-col items-center gap-1.5">
							<StepCircle isCompleted={isCompleted} isCurrent={isCurrent} stepNumber={stepNumber} />
							{label && (
								<span
									className={cn(
										'max-w-20 text-center text-[11px] leading-tight transition-all duration-300',
										isCurrent ? 'text-primary font-medium' : 'text-muted-foreground',
									)}
								>
									{label}
								</span>
							)}
						</div>
						{stepNumber < totalSteps && (
							<div className="relative mt-3.5 h-0.5 w-8 overflow-hidden bg-muted">
								<motion.div
									className="bg-primary absolute inset-y-0 left-0"
									initial={false}
									animate={{ width: stepNumber < step ? '100%' : '0%' }}
									transition={{ duration: 0.4, ease: 'easeInOut' }}
								/>
							</div>
						)}
					</div>
				);
			})}
		</div>
	);
}
